import {
  HeadContent,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { TanStackDevtools } from "@tanstack/react-devtools"
import { Toaster } from "@/components/ui/sonner"
import { Brand } from "@/components/public-navbar"

import appCss from "../styles.css?url"
import poppinsRegular from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url"
import poppinsBold from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url"

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "intoshort",
      },
    ],
    links: [
      ...[poppinsRegular, poppinsBold].map((href) => ({
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous" as const,
        href,
      })),
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=dragon-pink" },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32.png",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        sizes: "any",
        href: "/favicon.svg?v=dragon-pink",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png?v=dragon-pink",
      },
      { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#a522d8" },
      { rel: "manifest", href: "/manifest.json" },
    ],
  }),
  notFoundComponent: () => (
    <main className="container mx-auto p-4 pt-16">
      <h1>404</h1>
      <p>The requested page could not be found.</p>
    </main>
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const isApplication = pathname.startsWith("/app")
  const tracksPublicVisit = ["/", "/signin", "/signup"].includes(pathname)
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <div
          className="global-page-shell"
          data-public-page={isApplication ? undefined : "true"}
        >
          <div className="global-page-content">{children}</div>
          <footer className="landing-footer page-width">
            <div className="footer-brand">
              <a href="/" aria-label="intoshort, home">
                <Brand />
              </a>
              <p>From long videos to short clips.</p>
              <nav className="footer-legal" aria-label="Legal">
                <a href="/privacy-policy">Privacy policy</a>
                <a href="/terms-of-service">Terms of service</a>
              </nav>
              <span>© {new Date().getFullYear()} intoshort</span>
            </div>
            <div className="footer-sections">
              <nav className="footer-links" aria-labelledby="footer-resources">
                <h2 id="footer-resources">Resources</h2>
                <a href="/#questions">FAQ</a>
                <a href="/blog">Blog</a>
                <a href="/roadmap">Roadmap</a>
                <a href="/pricing">Pricing</a>
              </nav>
              <nav className="footer-links" aria-labelledby="footer-alternatives">
                <h2 id="footer-alternatives">Alternatives</h2>
                <a href="/opus-clip-alternative">Opus Clip</a>
                <a href="/2short-ai-alternative">2short.ai</a>
                <a href="/adcreative-ai-alternative">AdCreative.ai</a>
                <a href="/vizard-alternative">Vizard</a>
                <a href="/invideo-alternative">InVideo</a>
                <a href="/submagic-alternative">Submagic</a>
                <a href="/wayin-ai-alternative">Wayin AI</a>
              </nav>
              <nav className="footer-links" aria-labelledby="footer-tools">
                <h2 id="footer-tools">Tools</h2>
                <a href="/tools/long-video-to-short-video">Long video to shorts</a>
                <a href="/tools/youtube-to-shorts">YouTube to shorts</a>
                <a href="/tools/twitch-vod-to-shorts">Twitch VOD to shorts</a>
                <a href="/tools/gameplay-facecam-to-shorts">Gameplay &amp; facecam</a>
              </nav>
            </div>
          </footer>
        </div>
        {isApplication && <Toaster />}
        {import.meta.env.DEV && isApplication && (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )}
        <Scripts />
        {!import.meta.env.DEV && tracksPublicVisit && (
          <script
            type="module"
            src="https://static.cloudflareinsights.com/beacon.min.js"
            data-cf-beacon='{"token":"46bae16ea3b641e2883a43ba8383a7dc","spa":false}'
          />
        )}
      </body>
    </html>
  )
}
